/**
 * Tela de Leads e reabordagem, o que a Jaque mais usa no dia.
 *
 * A aba principal e "Reabordar hoje e atrasados": a fila do mais atrasado ao
 * de hoje, com as acoes no proprio item (registrar contato, adiar, marcar que
 * nao vale reabordar) para ela passar a lista sem abrir cada lead. "Leads"
 * busca e filtra todos, com o painel do lead e o historico em linha do tempo.
 * "Importar CSV" simula antes de gravar. Usa o kit de apps/web/src/ui.
 */
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EstadoCarregando, EstadoErro, EstadoVazio } from '../../layout/Shell.tsx';
import { useSessao } from '../../sessao.tsx';
import { ErroApi, escrever, ler, mensagemDe } from '../../ui/api.ts';
import {
  Aviso, Barra, BlocoInicio, Botao, Busca, Campo, Formulario, Painel, Selo, Tabela,
  type Coluna, type Tom,
} from '../../ui/componentes.tsx';
import { data, dataHora, hoje, relativo } from '../../ui/formatos.ts';
import type { TelaDeModulo } from '../contrato.ts';
import './leads.css';

// ---------------------------------------------------------------------------
// Tipos e rotulos
// ---------------------------------------------------------------------------

type Lead = {
  id: string;
  pessoa_id: string;
  nome: string;
  telefone: string | null;
  telefone_formatado: string | null;
  email: string | null;
  origem: Origem;
  oferta: Oferta | null;
  estagio: Estagio;
  motivo_pausa: Motivo | null;
  motivo_detalhe: string | null;
  vale_reabordar: boolean;
  proxima_reabordagem: string | null;
  ultimo_contato_em: string | null;
  responsavel_id: string | null;
  observacoes: string | null;
  criado_em: string;
  atualizado_em: string;
};

type Reabordagem = Lead & {
  dias_atraso: number;
  ultimo_contato_resumo: string | null;
  ultimo_contato_resultado: Resultado | null;
};

type Contato = {
  id: string;
  em: string;
  canal: Canal;
  resumo: string;
  resultado: Resultado;
  ator_tipo: 'usuario' | 'chave_api' | 'sistema';
};

const ORIGENS = {
  instagram: 'Instagram', indicacao: 'Indicação', site: 'Site', whatsapp: 'WhatsApp',
  evento: 'Evento', google: 'Google', importacao: 'Importação', outro: 'Outro',
} as const;
type Origem = keyof typeof ORIGENS;

const OFERTAS = { benini: 'Benini', formacao: 'Formação', revisao: 'Revisão', outro: 'Outro' } as const;
type Oferta = keyof typeof OFERTAS;

const ESTAGIOS = {
  novo: { rotulo: 'Novo', tom: 'info' },
  em_conversa: { rotulo: 'Em conversa', tom: 'marca' },
  proposta: { rotulo: 'Proposta', tom: 'aviso' },
  pausado: { rotulo: 'Pausado', tom: 'neutro' },
  matriculado: { rotulo: 'Matriculado', tom: 'ok' },
  perdido: { rotulo: 'Perdido', tom: 'erro' },
} as const satisfies Record<string, { rotulo: string; tom: Tom }>;
type Estagio = keyof typeof ESTAGIOS;

const MOTIVOS = {
  financeiro: 'Financeiro', agenda: 'Agenda', distancia: 'Distância',
  sem_resposta: 'Sem resposta', outro_curso: 'Fez outro curso', momento: 'Não é o momento',
  outro: 'Outro',
} as const;
type Motivo = keyof typeof MOTIVOS;

const CANAIS = {
  whatsapp: 'WhatsApp', telefone: 'Telefone', email: 'E-mail', instagram: 'Instagram',
  presencial: 'Presencial', outro: 'Outro',
} as const;
type Canal = keyof typeof CANAIS;

const RESULTADOS = {
  sem_resposta: { rotulo: 'Sem resposta', tom: 'neutro' },
  respondeu: { rotulo: 'Respondeu', tom: 'info' },
  interessado: { rotulo: 'Interessado', tom: 'marca' },
  agendou: { rotulo: 'Agendou', tom: 'ok' },
  pediu_pausa: { rotulo: 'Pediu pausa', tom: 'aviso' },
  fechou: { rotulo: 'Fechou', tom: 'ok' },
  desistiu: { rotulo: 'Desistiu', tom: 'erro' },
} as const satisfies Record<string, { rotulo: string; tom: Tom }>;
type Resultado = keyof typeof RESULTADOS;

/** AAAA-MM-DD + n dias. */
function somarDias(iso: string, dias: number): string {
  return new Date(Date.parse(`${iso}T00:00:00Z`) + dias * 86_400_000).toISOString().slice(0, 10);
}

function usePermissao(codigo: string): boolean {
  const { estado } = useSessao();
  return estado.fase === 'dentro' && estado.eu.permissoes.includes(codigo);
}

/**
 * O lead aberto vive na URL (?id=<uuid>), como em toda tela: link de tarefa,
 * do Inicio ou de outro modulo abre o painel direto. Fechar tira so o id.
 */
function useLeadDaUrl(): [string | null, (id: string) => void, () => void] {
  const [busca, setBusca] = useSearchParams();
  const id = busca.get('id');
  const mudar = (novo: string | null) => {
    const p = new URLSearchParams(busca);
    if (novo === null) p.delete('id');
    else p.set('id', novo);
    setBusca(p, { replace: true });
  };
  return [id, (novo) => mudar(novo), () => mudar(null)];
}

type Membro = { id: string; nome: string };

/** Equipe ativa do workspace, para o seletor de responsavel (rota do nucleo). */
function useEquipe() {
  return useQuery({
    queryKey: ['leads', 'equipe'],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const r = await fetch('/api/v1/auth/equipe', { credentials: 'same-origin' });
      if (!r.ok) throw new Error('Não foi possível carregar a equipe.');
      return ((await r.json()) as { itens: Membro[] }).itens;
    },
  });
}

/** Recarrega tudo de leads (fila, lista, painel, bloco do Inicio). */
function useAtualizar() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['leads'] });
}

function SeloEstagio({ estagio }: { estagio: Estagio }) {
  const e = ESTAGIOS[estagio];
  return <Selo tom={e.tom}>{e.rotulo}</Selo>;
}

function SeloAtraso({ dias }: { dias: number }) {
  if (dias <= 0) return <Selo tom="info">Hoje</Selo>;
  return <Selo tom="erro">{dias === 1 ? '1 dia de atraso' : `${dias} dias de atraso`}</Selo>;
}

function Opcoes<T extends string>({ rotulos }: { rotulos: Record<T, string | { rotulo: string }> }) {
  return (
    <>
      {(Object.entries(rotulos) as [T, string | { rotulo: string }][]).map(([v, r]) => (
        <option key={v} value={v}>{typeof r === 'string' ? r : r.rotulo}</option>
      ))}
    </>
  );
}

const erroDo = (erro: unknown, campo: string) =>
  (erro instanceof ErroApi ? erro.doCampo(campo) : undefined);

// ---------------------------------------------------------------------------
// Formularios curtos (no item da fila e no painel)
// ---------------------------------------------------------------------------

/** Registrar contato com resultado e a proxima data. */
function FormContato({ lead, aoConcluir, aoCancelar }: {
  lead: Pick<Lead, 'id' | 'estagio'>; aoConcluir: () => void; aoCancelar?: () => void;
}) {
  const atualizar = useAtualizar();
  const [canal, setCanal] = useState<Canal>('whatsapp');
  const [resultado, setResultado] = useState<Resultado>('sem_resposta');
  const [resumo, setResumo] = useState('');
  const [proxima, setProxima] = useState(somarDias(hoje(), 3));
  const [motivo, setMotivo] = useState<Motivo | ''>('');
  const encerra = resultado === 'desistiu';

  const salvar = useMutation({
    mutationFn: () => escrever('leads.contato_registrar', {
      lead_id: lead.id, canal, resultado, resumo,
      ...(!encerra && proxima !== '' ? { proxima_reabordagem: proxima } : {}),
      ...(resultado === 'pediu_pausa' && motivo !== '' ? { motivo_pausa: motivo } : {}),
    }),
    onSuccess: async () => { await atualizar(); aoConcluir(); },
  });

  return (
    <div className="leads-subform">
      {salvar.isError && <Aviso tom="erro">{mensagemDe(salvar.error)}</Aviso>}
      <Formulario aoEnviar={() => salvar.mutate()}>
        <Campo rotulo="Canal" largura="metade">
          {(id) => (
            <select id={id} value={canal} onChange={(e) => setCanal(e.target.value as Canal)}>
              <Opcoes rotulos={CANAIS} />
            </select>
          )}
        </Campo>
        <Campo rotulo="Resultado" largura="metade">
          {(id) => (
            <select id={id} value={resultado}
              onChange={(e) => setResultado(e.target.value as Resultado)}>
              <Opcoes rotulos={RESULTADOS} />
            </select>
          )}
        </Campo>
        <Campo rotulo="O que foi conversado" erro={erroDo(salvar.error, 'resumo')}>
          {(id) => (
            <textarea id={id} value={resumo} maxLength={2000}
              placeholder="Mandei as datas da turma de março e o valor à vista."
              onChange={(e) => setResumo(e.target.value)} />
          )}
        </Campo>
        {resultado === 'pediu_pausa' && (
          <Campo rotulo="Motivo da pausa" largura="metade" erro={erroDo(salvar.error, 'motivo_pausa')}>
            {(id) => (
              <select id={id} value={motivo} onChange={(e) => setMotivo(e.target.value as Motivo)}>
                <option value="">Escolha</option>
                <Opcoes rotulos={MOTIVOS} />
              </select>
            )}
          </Campo>
        )}
        {!encerra && (
          <Campo rotulo="Falar de novo em" largura="metade"
            erro={erroDo(salvar.error, 'proxima_reabordagem')}
            dica={proxima !== '' ? relativo(proxima) : 'Sem data, o lead continua na fila de hoje.'}>
            {(id) => (
              <input id={id} type="date" value={proxima} min={hoje()}
                onChange={(e) => setProxima(e.target.value)} />
            )}
          </Campo>
        )}
        {encerra && (
          <Aviso tom="aviso">Desistiu marca o lead como perdido e tira da reabordagem.</Aviso>
        )}
        <div className="leads-subform__acoes">
          {aoCancelar !== undefined && <Botao variante="fantasma" aoClicar={aoCancelar}>Cancelar</Botao>}
          <Botao variante="primario" tipo="submit" desativado={salvar.isPending || resumo.trim() === ''}>
            {salvar.isPending ? 'Salvando' : 'Registrar contato'}
          </Botao>
        </div>
      </Formulario>
    </div>
  );
}

/** Marcar que nao vale reabordar, com motivo. */
function FormDescartar({ lead, aoConcluir, aoCancelar }: {
  lead: Pick<Lead, 'id'>; aoConcluir: () => void; aoCancelar: () => void;
}) {
  const atualizar = useAtualizar();
  const [motivo, setMotivo] = useState<Motivo | ''>('');
  const [detalhe, setDetalhe] = useState('');
  const salvar = useMutation({
    mutationFn: () => escrever('leads.editar', {
      id: lead.id, vale_reabordar: false, motivo_pausa: motivo,
      motivo_detalhe: detalhe.trim() === '' ? null : detalhe, proxima_reabordagem: null,
    }),
    onSuccess: async () => { await atualizar(); aoConcluir(); },
  });
  return (
    <div className="leads-subform">
      {salvar.isError && <Aviso tom="erro">{mensagemDe(salvar.error)}</Aviso>}
      <Formulario aoEnviar={() => salvar.mutate()}>
        <Campo rotulo="Por que não vale reabordar" largura="metade">
          {(id) => (
            <select id={id} value={motivo} onChange={(e) => setMotivo(e.target.value as Motivo)}>
              <option value="">Escolha o motivo</option>
              <Opcoes rotulos={MOTIVOS} />
            </select>
          )}
        </Campo>
        <Campo rotulo="Detalhe (opcional)" largura="metade">
          {(id) => (
            <input id={id} value={detalhe} maxLength={500}
              placeholder="Mora longe e não viaja" onChange={(e) => setDetalhe(e.target.value)} />
          )}
        </Campo>
        <div className="leads-subform__acoes">
          <Botao variante="fantasma" aoClicar={aoCancelar}>Cancelar</Botao>
          <Botao variante="perigo" tipo="submit" desativado={salvar.isPending || motivo === ''}>
            Não vale reabordar
          </Botao>
        </div>
      </Formulario>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Aba Reabordar hoje e atrasados
// ---------------------------------------------------------------------------

function ItemReabordar({ item, aoAbrir }: { item: Reabordagem; aoAbrir: () => void }) {
  const atualizar = useAtualizar();
  const podeEditar = usePermissao('leads.editar');
  const [aberto, setAberto] = useState<'contato' | 'descartar' | null>(null);
  const adiar = useMutation({
    mutationFn: (dias: number) => escrever('leads.agendar_reabordagem', { id: item.id, dias }),
    onSuccess: atualizar,
  });
  const atrasado = item.dias_atraso > 0;

  return (
    <article className={`leads-cartao leads-cartao--${atrasado ? 'atrasado' : 'hoje'}`}>
      <div className="leads-cartao__topo">
        <div className="leads-cartao__quem">
          <button type="button" className="leads-cartao__nome" onClick={aoAbrir}>{item.nome}</button>
          <span className="leads-cartao__contato">
            {[item.telefone_formatado, item.email].filter(Boolean).join(' · ')}
          </span>
        </div>
        <div className="leads-cartao__selos">
          <SeloAtraso dias={item.dias_atraso} />
          {item.oferta !== null && <Selo tom="marca">{OFERTAS[item.oferta]}</Selo>}
          <SeloEstagio estagio={item.estagio} />
        </div>
      </div>
      <p className="leads-cartao__ultimo">
        {item.ultimo_contato_em === null
          ? 'Ainda sem contato registrado.'
          : <>
            <strong>Último contato {relativo(item.ultimo_contato_em)}</strong>
            {item.ultimo_contato_resultado !== null
              && `, ${RESULTADOS[item.ultimo_contato_resultado].rotulo.toLowerCase()}`}
            {item.ultimo_contato_resumo !== null && `: ${item.ultimo_contato_resumo}`}
          </>}
      </p>
      {adiar.isError && <Aviso tom="erro">{mensagemDe(adiar.error)}</Aviso>}
      {podeEditar && aberto === null && (
        <div className="leads-cartao__acoes">
          <Botao variante="primario" aoClicar={() => setAberto('contato')}>Registrar contato</Botao>
          <Botao desativado={adiar.isPending} aoClicar={() => adiar.mutate(1)}>Amanhã</Botao>
          <Botao desativado={adiar.isPending} aoClicar={() => adiar.mutate(3)}>+3 dias</Botao>
          <Botao desativado={adiar.isPending} aoClicar={() => adiar.mutate(7)}>+7 dias</Botao>
          <Botao variante="fantasma" aoClicar={() => setAberto('descartar')}>Não vale reabordar</Botao>
        </div>
      )}
      {aberto === 'contato' && (
        <FormContato lead={item} aoConcluir={() => setAberto(null)} aoCancelar={() => setAberto(null)} />
      )}
      {aberto === 'descartar' && (
        <FormDescartar lead={item} aoConcluir={() => setAberto(null)} aoCancelar={() => setAberto(null)} />
      )}
    </article>
  );
}

type Fila = { hoje: string; total: number; itens: Reabordagem[] };

function AbaReabordar() {
  const [aberto, abrir, fechar] = useLeadDaUrl();
  const q = useQuery({
    queryKey: ['leads', 'reabordar'],
    queryFn: () => ler<Fila>('leads.reabordagens_do_dia', { limite: 500 }),
  });
  const atrasados = q.data?.itens.filter((i) => i.dias_atraso > 0).length ?? 0;

  return (
    <>
      {q.isPending && <EstadoCarregando texto="Carregando a fila de hoje" />}
      {q.isError && <EstadoErro titulo="Não foi possível carregar" texto={mensagemDe(q.error)} />}
      {q.isSuccess && q.data.total === 0 && (
        <EstadoVazio titulo="Ninguém para reabordar hoje"
          texto="Quando chegar o dia de falar de novo com um lead, ele aparece aqui." />
      )}
      {q.isSuccess && q.data.total > 0 && (
        <>
          <div className="leads-resumo">
            <Selo tom="marca">{q.data.total} para reabordar</Selo>
            {atrasados > 0 && <Selo tom="erro">{atrasados} atrasado(s)</Selo>}
            <span>Do mais atrasado ao de hoje, {data(q.data.hoje)}.</span>
          </div>
          <div className="leads-fila">
            {q.data.itens.map((i) => (
              <ItemReabordar key={i.id} item={i} aoAbrir={() => abrir(i.id)} />
            ))}
          </div>
        </>
      )}
      {aberto !== null && <PainelLead key={aberto} id={aberto} aoFechar={fechar} />}
    </>
  );
}

// ---------------------------------------------------------------------------
// Painel do lead
// ---------------------------------------------------------------------------

type FormLead = {
  estagio: Estagio;
  oferta: Oferta | '';
  origem: Origem;
  motivo_pausa: Motivo | '';
  motivo_detalhe: string;
  vale_reabordar: boolean;
  proxima_reabordagem: string;
  responsavel_id: string;
  observacoes: string;
};

const doLead = (l: Lead): FormLead => ({
  estagio: l.estagio,
  oferta: l.oferta ?? '',
  origem: l.origem,
  motivo_pausa: l.motivo_pausa ?? '',
  motivo_detalhe: l.motivo_detalhe ?? '',
  vale_reabordar: l.vale_reabordar,
  proxima_reabordagem: l.proxima_reabordagem ?? '',
  responsavel_id: l.responsavel_id ?? '',
  observacoes: l.observacoes ?? '',
});

function LinhaDoTempo({ contatos }: { contatos: Contato[] }) {
  if (contatos.length === 0) {
    return <p className="ui-item__detalhe">Nenhum contato registrado ainda.</p>;
  }
  return (
    <ol className="leads-linha-tempo">
      {contatos.map((c) => (
        <li key={c.id} className="leads-linha-tempo__item">
          <div className="leads-linha-tempo__cabeca">
            <span>{dataHora(c.em)}</span>
            <span>{CANAIS[c.canal]}</span>
            <Selo tom={RESULTADOS[c.resultado].tom}>{RESULTADOS[c.resultado].rotulo}</Selo>
            {c.ator_tipo === 'chave_api' && <span>pelo Igor</span>}
          </div>
          <p className="leads-linha-tempo__resumo">{c.resumo}</p>
        </li>
      ))}
    </ol>
  );
}

/** `salvo` mora no painel: salvar remonta este formulario (key = atualizado_em). */
function EdicaoLead({ lead, salvo, setSalvo }: {
  lead: Lead; salvo: boolean; setSalvo: (v: boolean) => void;
}) {
  const atualizar = useAtualizar();
  const podeEditar = usePermissao('leads.editar');
  const [form, setForm] = useState<FormLead>(() => doLead(lead));
  const equipe = useEquipe();
  const pedeMotivo = form.estagio === 'pausado' || !form.vale_reabordar;

  const salvar = useMutation({
    mutationFn: () => escrever<Lead>('leads.editar', {
      id: lead.id,
      estagio: form.estagio,
      origem: form.origem,
      oferta: form.oferta === '' ? null : form.oferta,
      vale_reabordar: form.vale_reabordar,
      responsavel_id: form.responsavel_id === '' ? null : form.responsavel_id,
      // so manda a data quando mudou: data antiga ja vencida continua valendo
      ...(form.proxima_reabordagem !== (lead.proxima_reabordagem ?? '')
        ? { proxima_reabordagem: form.proxima_reabordagem === '' ? null : form.proxima_reabordagem }
        : {}),
      ...(pedeMotivo ? {
        motivo_pausa: form.motivo_pausa === '' ? null : form.motivo_pausa,
        motivo_detalhe: form.motivo_detalhe.trim() === '' ? null : form.motivo_detalhe,
      } : {}),
      observacoes: form.observacoes.trim() === '' ? null : form.observacoes,
    }),
    onSuccess: async () => { await atualizar(); setSalvo(true); },
    onError: () => setSalvo(false),
  });
  const mudar = <K extends keyof FormLead>(k: K, v: FormLead[K]) => {
    setSalvo(false);
    setForm((f) => ({ ...f, [k]: v }));
  };

  return (
    <>
      {salvo && <Aviso tom="ok">Alterações salvas.</Aviso>}
      {salvar.isError && <Aviso tom="erro">{mensagemDe(salvar.error)}</Aviso>}
      <Formulario aoEnviar={() => salvar.mutate()}>
        <Campo rotulo="Estágio" largura="metade">
          {(id) => (
            <select id={id} value={form.estagio} disabled={!podeEditar}
              onChange={(e) => mudar('estagio', e.target.value as Estagio)}>
              <Opcoes rotulos={ESTAGIOS} />
            </select>
          )}
        </Campo>
        <Campo rotulo="Curso de interesse" largura="metade">
          {(id) => (
            <select id={id} value={form.oferta} disabled={!podeEditar}
              onChange={(e) => mudar('oferta', e.target.value as Oferta | '')}>
              <option value="">Ainda não sabe</option>
              <Opcoes rotulos={OFERTAS} />
            </select>
          )}
        </Campo>
        <Campo rotulo="Origem" largura="metade">
          {(id) => (
            <select id={id} value={form.origem} disabled={!podeEditar}
              onChange={(e) => mudar('origem', e.target.value as Origem)}>
              <Opcoes rotulos={ORIGENS} />
            </select>
          )}
        </Campo>
        <Campo rotulo="Próxima reabordagem" largura="metade"
          erro={erroDo(salvar.error, 'proxima_reabordagem')}
          {...(form.proxima_reabordagem !== '' ? { dica: relativo(form.proxima_reabordagem) } : {})}>
          {(id) => (
            <input id={id} type="date" value={form.proxima_reabordagem} disabled={!podeEditar}
              onChange={(e) => mudar('proxima_reabordagem', e.target.value)} />
          )}
        </Campo>
        <Campo rotulo="Responsável" largura="metade"
          {...(equipe.isError ? { erro: mensagemDe(equipe.error) } : {})}>
          {(id) => (
            <select id={id} value={form.responsavel_id} disabled={!podeEditar || equipe.isPending}
              onChange={(e) => mudar('responsavel_id', e.target.value)}>
              <option value="">Ninguém</option>
              {(equipe.data ?? []).map((m) => <option key={m.id} value={m.id}>{m.nome}</option>)}
              {/* responsavel que saiu da equipe continua aparecendo */}
              {form.responsavel_id !== '' && equipe.isSuccess
                && !equipe.data.some((m) => m.id === form.responsavel_id) && (
                <option value={form.responsavel_id}>Fora da equipe</option>
              )}
            </select>
          )}
        </Campo>
        <Campo rotulo="Vale reabordar?" largura="metade">
          {(id) => (
            <select id={id} value={form.vale_reabordar ? 'sim' : 'nao'} disabled={!podeEditar}
              onChange={(e) => mudar('vale_reabordar', e.target.value === 'sim')}>
              <option value="sim">Sim</option>
              <option value="nao">Não</option>
            </select>
          )}
        </Campo>
        {pedeMotivo && (
          <Campo rotulo="Motivo" largura="metade" erro={erroDo(salvar.error, 'motivo_pausa')}>
            {(id) => (
              <select id={id} value={form.motivo_pausa} disabled={!podeEditar}
                onChange={(e) => mudar('motivo_pausa', e.target.value as Motivo | '')}>
                <option value="">Escolha o motivo</option>
                <Opcoes rotulos={MOTIVOS} />
              </select>
            )}
          </Campo>
        )}
        {pedeMotivo && (
          <Campo rotulo="Detalhe do motivo">
            {(id) => (
              <input id={id} value={form.motivo_detalhe} maxLength={500} disabled={!podeEditar}
                onChange={(e) => mudar('motivo_detalhe', e.target.value)} />
            )}
          </Campo>
        )}
        <Campo rotulo="Observações" erro={erroDo(salvar.error, 'observacoes')}>
          {(id) => (
            <textarea id={id} value={form.observacoes} maxLength={2000} disabled={!podeEditar}
              onChange={(e) => mudar('observacoes', e.target.value)} />
          )}
        </Campo>
        {podeEditar && (
          <div className="leads-subform__acoes">
            <Botao variante="primario" tipo="submit" desativado={salvar.isPending}>
              {salvar.isPending ? 'Salvando' : 'Salvar lead'}
            </Botao>
          </div>
        )}
      </Formulario>
    </>
  );
}

function PainelLead({ id, aoFechar }: { id: string; aoFechar: () => void }) {
  const atualizar = useAtualizar();
  const podeEditar = usePermissao('leads.editar');
  const podeExcluir = usePermissao('leads.excluir');
  const [salvo, setSalvo] = useState(false);
  const q = useQuery({
    queryKey: ['leads', 'lead', id],
    queryFn: () => ler<{ lead: Lead; contatos: Contato[] }>('leads.obter', { id }),
  });
  const excluir = useMutation({
    mutationFn: () => escrever('leads.excluir', { id }),
    onSuccess: async () => { await atualizar(); aoFechar(); },
  });
  const lead = q.data?.lead;

  return (
    <Painel aberto aoFechar={aoFechar} titulo={lead?.nome ?? 'Lead'}
      {...(lead !== undefined ? {
        subtitulo: [lead.telefone_formatado, lead.email, `chegou por ${ORIGENS[lead.origem]}`]
          .filter(Boolean).join(' · '),
      } : {})}
      {...(lead !== undefined && podeExcluir ? {
        rodape: (
          <Botao variante="perigo" desativado={excluir.isPending}
            aoClicar={() => { if (window.confirm(`Excluir o lead de ${lead.nome}?`)) excluir.mutate(); }}>
            Excluir lead
          </Botao>
        ),
      } : {})}
    >
      {q.isPending && <EstadoCarregando texto="Carregando o lead" />}
      {q.isError && <EstadoErro titulo="Não foi possível carregar" texto={mensagemDe(q.error)} />}
      {excluir.isError && <Aviso tom="erro">{mensagemDe(excluir.error)}</Aviso>}
      {q.isSuccess && lead !== undefined && (
        <>
          <div className="leads-cartao__selos">
            <SeloEstagio estagio={lead.estagio} />
            {lead.oferta !== null && <Selo tom="marca">{OFERTAS[lead.oferta]}</Selo>}
            {!lead.vale_reabordar && <Selo tom="neutro">Não vale reabordar</Selo>}
          </div>
          <section className="leads-secao">
            <h3 className="leads-secao__titulo">Histórico de contatos</h3>
            <LinhaDoTempo contatos={q.data.contatos} />
          </section>
          {podeEditar && (
            <section className="leads-secao">
              <h3 className="leads-secao__titulo">Registrar contato</h3>
              <FormContato key={q.data.contatos.length} lead={lead} aoConcluir={() => {}} />
            </section>
          )}
          <section className="leads-secao">
            <h3 className="leads-secao__titulo">Dados do lead</h3>
            <EdicaoLead key={lead.atualizado_em} lead={lead} salvo={salvo} setSalvo={setSalvo} />
          </section>
        </>
      )}
    </Painel>
  );
}

function PainelNovoLead({ aoFechar, aoCriar }: {
  aoFechar: () => void; aoCriar: (id: string, aviso: string) => void;
}) {
  const atualizar = useAtualizar();
  const [form, setForm] = useState({
    nome: '', telefone: '', email: '', origem: 'whatsapp' as Origem, oferta: '' as Oferta | '',
    observacoes: '',
  });
  const salvar = useMutation({
    mutationFn: () => escrever<{ lead: Lead; criado: boolean }>('leads.registrar', {
      nome: form.nome, origem: form.origem,
      telefone: form.telefone.trim() === '' ? null : form.telefone,
      email: form.email.trim() === '' ? null : form.email,
      ...(form.oferta !== '' ? { oferta: form.oferta } : {}),
      ...(form.observacoes.trim() !== '' ? { observacoes: form.observacoes } : {}),
    }),
    onSuccess: async (r) => {
      await atualizar();
      aoCriar(r.lead.id, r.criado
        ? 'Lead registrado. Ele já está na fila de hoje.'
        : 'Essa pessoa já tinha um lead aberto; ele foi atualizado.');
    },
  });
  const campo = (k: 'nome' | 'telefone' | 'email', extra: Record<string, unknown> = {}) =>
    (id: string) => (
      <input id={id} value={form[k]} {...extra}
        onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} />
    );

  return (
    <Painel aberto aoFechar={aoFechar} titulo="Novo lead"
      subtitulo="Se a pessoa já existir pelo telefone ou e-mail, o cadastro é reaproveitado."
      rodape={(
        <Botao variante="primario" desativado={salvar.isPending} aoClicar={() => salvar.mutate()}>
          {salvar.isPending ? 'Salvando' : 'Registrar lead'}
        </Botao>
      )}>
      {salvar.isError && <Aviso tom="erro">{mensagemDe(salvar.error)}</Aviso>}
      <Formulario aoEnviar={() => salvar.mutate()}>
        <Campo rotulo="Nome" erro={erroDo(salvar.error, 'nome')}>{campo('nome', { autoComplete: 'off' })}</Campo>
        <Campo rotulo="Telefone (WhatsApp)" largura="metade" erro={erroDo(salvar.error, 'telefone')}>
          {campo('telefone', { inputMode: 'tel', placeholder: '(11) 98765-4321' })}
        </Campo>
        <Campo rotulo="E-mail" largura="metade" erro={erroDo(salvar.error, 'email')}>
          {campo('email', { type: 'email' })}
        </Campo>
        <Campo rotulo="Origem" largura="metade">
          {(id) => (
            <select id={id} value={form.origem}
              onChange={(e) => setForm((f) => ({ ...f, origem: e.target.value as Origem }))}>
              <Opcoes rotulos={ORIGENS} />
            </select>
          )}
        </Campo>
        <Campo rotulo="Curso de interesse" largura="metade">
          {(id) => (
            <select id={id} value={form.oferta}
              onChange={(e) => setForm((f) => ({ ...f, oferta: e.target.value as Oferta | '' }))}>
              <option value="">Ainda não sabe</option>
              <Opcoes rotulos={OFERTAS} />
            </select>
          )}
        </Campo>
        <Campo rotulo="Observações">
          {(id) => (
            <textarea id={id} value={form.observacoes} maxLength={2000}
              onChange={(e) => setForm((f) => ({ ...f, observacoes: e.target.value }))} />
          )}
        </Campo>
        <button type="submit" hidden />
      </Formulario>
    </Painel>
  );
}

// ---------------------------------------------------------------------------
// Aba Leads
// ---------------------------------------------------------------------------

const COLUNAS: readonly Coluna<Lead>[] = [
  {
    id: 'nome', titulo: 'Nome',
    celula: (l) => (
      <span className="ui-item__principal">
        <strong>{l.nome}</strong>
        <span className="ui-item__detalhe">{l.telefone_formatado ?? l.email ?? ''}</span>
      </span>
    ),
  },
  { id: 'oferta', titulo: 'Curso', celula: (l) => (l.oferta !== null ? OFERTAS[l.oferta] : '') },
  { id: 'estagio', titulo: 'Estágio', celula: (l) => <SeloEstagio estagio={l.estagio} /> },
  {
    id: 'proxima', titulo: 'Próxima reabordagem',
    celula: (l) => (!l.vale_reabordar ? 'Não vale reabordar'
      : l.proxima_reabordagem === null ? ''
        : `${data(l.proxima_reabordagem)} (${relativo(l.proxima_reabordagem)})`),
  },
  {
    id: 'ultimo', titulo: 'Último contato', secundaria: true,
    celula: (l) => (l.ultimo_contato_em !== null ? relativo(l.ultimo_contato_em) : 'Nenhum'),
  },
  { id: 'origem', titulo: 'Origem', secundaria: true, celula: (l) => ORIGENS[l.origem] },
];

function AbaLeads() {
  const podeCriar = usePermissao('leads.criar');
  const [termo, setTermo] = useState('');
  const [estagio, setEstagio] = useState<Estagio | ''>('');
  const [oferta, setOferta] = useState<Oferta | ''>('');
  const [origem, setOrigem] = useState<Origem | ''>('');
  const [aberto, abrir, fechar] = useLeadDaUrl();
  const [novo, setNovo] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ['leads', 'lista', termo, estagio, oferta, origem],
    queryFn: () => ler<{ itens: Lead[] }>('leads.buscar', {
      termo, estagio, oferta, origem, limite: 500,
    }),
  });
  const filtrando = termo !== '' || estagio !== '' || oferta !== '' || origem !== '';

  return (
    <>
      <Barra acoes={podeCriar
        ? <Botao variante="primario" aoClicar={() => { setAviso(null); setNovo(true); }}>Novo lead</Botao>
        : undefined}>
        <Busca valor={termo} aoMudar={setTermo} dica="Buscar por nome, telefone ou e-mail" />
        <select aria-label="Estágio" value={estagio}
          onChange={(e) => setEstagio(e.target.value as Estagio | '')}>
          <option value="">Todos os estágios</option>
          <Opcoes rotulos={ESTAGIOS} />
        </select>
        <select aria-label="Curso" value={oferta} onChange={(e) => setOferta(e.target.value as Oferta | '')}>
          <option value="">Todos os cursos</option>
          <Opcoes rotulos={OFERTAS} />
        </select>
        <select aria-label="Origem" value={origem} onChange={(e) => setOrigem(e.target.value as Origem | '')}>
          <option value="">Todas as origens</option>
          <Opcoes rotulos={ORIGENS} />
        </select>
      </Barra>
      {aviso !== null && <Aviso tom="ok">{aviso}</Aviso>}

      {q.isPending && <EstadoCarregando texto="Carregando leads" />}
      {q.isError && <EstadoErro titulo="Não foi possível carregar" texto={mensagemDe(q.error)} />}
      {q.isSuccess && q.data.itens.length === 0 && (
        <EstadoVazio titulo={filtrando ? 'Nenhum lead encontrado' : 'Nenhum lead ainda'}
          texto={filtrando ? 'Tente outro nome, telefone ou filtro.'
            : 'Registre o primeiro lead ou importe a sua planilha na aba Importar CSV.'} />
      )}
      {q.isSuccess && q.data.itens.length > 0 && (
        <Tabela colunas={COLUNAS} linhas={q.data.itens} chave={(l) => l.id}
          aoAbrir={(l) => abrir(l.id)} />
      )}

      {novo && (
        <PainelNovoLead aoFechar={() => setNovo(false)}
          aoCriar={(id, texto) => { setAviso(texto); setNovo(false); abrir(id); }} />
      )}
      {aberto !== null && <PainelLead key={aberto} id={aberto} aoFechar={fechar} />}
    </>
  );
}

// ---------------------------------------------------------------------------
// Aba Importar CSV
// ---------------------------------------------------------------------------

type LinhaRelatorio = {
  linha: number;
  nome: string;
  acao: 'criado' | 'atualizado' | 'erro';
  pessoa_criada: boolean;
  falhas: { campo: string | null; mensagem: string }[];
};

type Relatorio = {
  simulacao: boolean;
  total: number;
  criados: number;
  atualizados: number;
  erros: { linha: number }[];
  linhas: LinhaRelatorio[];
};

const ACAO: Record<LinhaRelatorio['acao'], { rotulo: string; tom: Tom }> = {
  criado: { rotulo: 'Novo lead', tom: 'ok' },
  atualizado: { rotulo: 'Atualiza lead', tom: 'info' },
  erro: { rotulo: 'Com erro', tom: 'erro' },
};

const COLUNAS_RELATORIO: readonly Coluna<LinhaRelatorio>[] = [
  { id: 'linha', titulo: 'Linha', celula: (l) => l.linha },
  { id: 'nome', titulo: 'Nome', celula: (l) => l.nome },
  {
    id: 'acao', titulo: 'Resultado',
    celula: (l) => <Selo tom={ACAO[l.acao].tom}>{ACAO[l.acao].rotulo}</Selo>,
  },
  {
    id: 'detalhe', titulo: 'Detalhe',
    celula: (l) => (l.falhas.length > 0
      ? <ul className="leads-falhas">{l.falhas.map((f, i) => <li key={i}>{f.mensagem}</li>)}</ul>
      : l.pessoa_criada ? 'Pessoa nova' : 'Pessoa já cadastrada'),
  },
];

const CABECALHO = 'nome;telefone;email;origem;oferta;estagio;motivo_pausa;vale_reabordar;'
  + 'proxima_reabordagem;observacoes';

function AbaImportar() {
  const atualizar = useAtualizar();
  const [arquivo, setArquivo] = useState('leads.csv');
  const [conteudo, setConteudo] = useState('');
  const [simulado, setSimulado] = useState<{ conteudo: string; relatorio: Relatorio } | null>(null);
  const [gravado, setGravado] = useState<Relatorio | null>(null);

  const rodar = useMutation({
    mutationFn: (simular: boolean) => escrever<Relatorio>('leads.importar_csv',
      { arquivo_nome: arquivo, conteudo, simular }),
    onSuccess: async (r) => {
      if (r.simulacao) {
        setSimulado({ conteudo, relatorio: r });
        setGravado(null);
      } else {
        setGravado(r);
        setSimulado(null);
        await atualizar();
      }
    },
  });

  const lerArquivo = async (f: File | undefined) => {
    if (f === undefined) return;
    setArquivo(f.name);
    setConteudo(await f.text());
    setSimulado(null);
    setGravado(null);
  };
  const relatorio = gravado ?? simulado?.relatorio ?? null;
  const podeGravar = simulado !== null && simulado.conteudo === conteudo
    && simulado.relatorio.criados + simulado.relatorio.atualizados > 0;

  return (
    <div className="leads-importar">
      <div className="leads-importar__arquivo">
        <p className="leads-importar__dica">
          Separador <code>;</code> ou <code>,</code>. Cabeçalho: <code>{CABECALHO}</code>. Datas em
          dd/mm/aaaa ou aaaa-mm-dd; vale_reabordar sim ou não. Primeiro simule: nada é gravado até
          você confirmar.
        </p>
        <Campo rotulo="Arquivo CSV">
          {(id) => (
            <input id={id} type="file" accept=".csv,text/csv"
              onChange={(e) => { void lerArquivo(e.target.files?.[0]); }} />
          )}
        </Campo>
        <Campo rotulo="Ou cole o conteúdo">
          {(id) => (
            <textarea id={id} value={conteudo} spellCheck={false} placeholder={CABECALHO}
              onChange={(e) => setConteudo(e.target.value)} />
          )}
        </Campo>
        <Barra acoes={(
          <>
            <Botao desativado={rodar.isPending || conteudo.trim() === ''}
              aoClicar={() => rodar.mutate(true)}>Simular importação</Botao>
            <Botao variante="primario" desativado={rodar.isPending || !podeGravar}
              titulo={podeGravar ? 'Gravar o que a simulação mostrou' : 'Simule antes de importar'}
              aoClicar={() => rodar.mutate(false)}>
              {simulado !== null && podeGravar
                ? `Importar ${simulado.relatorio.criados + simulado.relatorio.atualizados} linha(s)`
                : 'Importar'}
            </Botao>
          </>
        )} />
      </div>

      {rodar.isPending && <EstadoCarregando texto="Lendo o arquivo" />}
      {rodar.isError && <Aviso tom="erro">{mensagemDe(rodar.error)}</Aviso>}
      {simulado !== null && simulado.conteudo !== conteudo && (
        <Aviso tom="aviso">O conteúdo mudou depois da simulação. Simule de novo.</Aviso>
      )}
      {relatorio !== null && !rodar.isPending && (
        <>
          <Aviso tom={relatorio.simulacao ? 'info' : 'ok'}>
            {relatorio.simulacao
              ? 'Simulação: nada foi gravado. Confira as linhas e clique em Importar.'
              : 'Importação concluída.'}
          </Aviso>
          <div className="leads-numeros">
            <Selo tom="neutro">{relatorio.total} linha(s)</Selo>
            <Selo tom="ok">{relatorio.criados} novo(s)</Selo>
            <Selo tom="info">{relatorio.atualizados} atualizado(s)</Selo>
            <Selo tom={relatorio.linhas.some((l) => l.acao === 'erro') ? 'erro' : 'neutro'}>
              {relatorio.linhas.filter((l) => l.acao === 'erro').length} com erro
            </Selo>
          </div>
          <Tabela colunas={COLUNAS_RELATORIO} linhas={relatorio.linhas} chave={(l) => String(l.linha)} />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bloco do Inicio
// ---------------------------------------------------------------------------

function BlocoReabordar() {
  const q = useQuery({
    queryKey: ['leads', 'reabordar', 'inicio'],
    queryFn: () => ler<Fila>('leads.reabordagens_do_dia', { limite: 5 }),
  });
  const total = q.data?.total ?? 0;
  return (
    <BlocoInicio titulo="Reabordar hoje" numero={total} tom={total > 0 ? 'aviso' : 'ok'}
      link={{ rotulo: 'Abrir a fila', href: '/leads?aba=reabordar' }}>
      {q.isPending && <span className="ui-item__detalhe">Carregando</span>}
      {q.isError && <span className="ui-item__detalhe">{mensagemDe(q.error)}</span>}
      {q.isSuccess && total === 0 && (
        <span className="ui-item__detalhe">Ninguém para reabordar hoje.</span>
      )}
      {q.data?.itens.map((i) => (
        <div key={i.id} className="ui-item">
          <div className="ui-item__principal">
            <Link className="ui-item__nome" to={`/leads?aba=reabordar&id=${i.id}`}>{i.nome}</Link>
            <span className="ui-item__detalhe">
              {[i.oferta !== null ? OFERTAS[i.oferta] : null, ESTAGIOS[i.estagio].rotulo]
                .filter(Boolean).join(' · ')}
            </span>
          </div>
          <SeloAtraso dias={i.dias_atraso} />
        </div>
      ))}
    </BlocoInicio>
  );
}

export const tela: TelaDeModulo = {
  slug: 'leads',
  rota: '/leads',
  kicker: 'Funil de matrícula',
  titulo: 'Leads e reabordagem',
  abas: [
    { id: 'reabordar', rotulo: 'Reabordar hoje e atrasados', conteudo: AbaReabordar,
      permissao: 'leads.ver' },
    { id: 'leads', rotulo: 'Leads', conteudo: AbaLeads, permissao: 'leads.ver' },
    { id: 'importar', rotulo: 'Importar CSV', conteudo: AbaImportar, permissao: 'leads.importar' },
  ],
  blocosDoInicio: [{ id: 'reabordar', permissao: 'leads.ver', conteudo: BlocoReabordar }],
  dicionario: { pt: {} },
};
